// Sidebar.jsx
import { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Ticket,
  Users,
  Settings,
  BarChart3,
  Plus,
  ChevronDown,
  ChevronRight,
  User,
  Building2,
  Wrench
} from 'lucide-react';
import { useAuth } from '@context/AuthContext';

const Sidebar = ({ isOpen, onClose }) => {
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isDesktop, setIsDesktop] = useState(false);
  const location = useLocation();
  const { user, hasRole } = useAuth();
  const pathname = location.pathname;

  const menuItems = useMemo(() => [
    {
      key: 'dashboard',
      label: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      roles: ['ADMIN', 'USER', 'CUSTOMER', 'SUPPLIER']
    },
    {
      key: 'tickets',
      label: 'Tickets',
      icon: Ticket,
      roles: ['ADMIN', 'USER', 'CUSTOMER', 'SUPPLIER'],
      submenu: [
        { label: 'All Tickets', href: '/tickets', icon: Ticket },
        { label: 'Create Ticket', href: '/tickets/create', icon: Plus }
        // { label: 'My Tickets', href: '/tickets?assigned=me', icon: User }
      ]
    },
    {
      key: 'users',
      label: 'Users',
      href: '/users',
      icon: Users,
      roles: ['ADMIN']
    },
    {
      key: 'analytics',
      label: 'Analytics',
      href: '/analytics',
      icon: BarChart3,
      roles: ['ADMIN', 'USER']
    },
    {
      key: 'settings',
      label: 'Settings',
      href: '/settings',
      icon: Settings,
      roles: ['ADMIN']
    }
  ], []);

  // Safe hasRole usage — if hasRole is a function use it, otherwise allow by default if user exists
  const checkRole = useMemo(
    () => (role) => (typeof hasRole === 'function' ? hasRole(role) : Boolean(user)),
    [hasRole, user]
  );

  const filteredMenuItems = useMemo(
    () => menuItems.filter(item => (item.roles || []).some(role => checkRole(role))),
    [menuItems, checkRole]
  );

  // Check if current window is desktop size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Safer isActive check (avoid /tickets-old matching /tickets)
  const isActive = (href) => {
    if (!href) return false;
    // match full path or path with trailing segments or ignore query string
    const hrefPath = href.split('?')[0].replace(/\/$/, '');
    const currentPath = pathname.split('?')[0].replace(/\/$/, '');
    return currentPath === hrefPath;
  };

  // Ensure submenu that contains current route is expanded on load or route change
  useEffect(() => {
    const newExpanded = {};
    filteredMenuItems.forEach(item => {
      if (!item.submenu) return;
      const match = item.submenu.some(sub => isActive(sub.href));
      if (match) newExpanded[item.key] = true;
    });

    // Only update if there are changes to avoid infinite loop
    if (Object.keys(newExpanded).length > 0) {
      setExpandedMenus(prev => ({ ...prev, ...newExpanded }));
    }
  }, [pathname, filteredMenuItems]);

  // Handle window resize - close sidebar on desktop if needed
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isOpen) {
        // Don't auto-close on desktop, let parent component handle
        // onClose();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen && !isDesktop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isDesktop]);

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({ ...prev, [menuKey]: !prev[menuKey] }));
  };

  const handleLinkClick = () => {
    // Only close sidebar on mobile
    if (!isDesktop && typeof onClose === 'function') {
      onClose();
    }
  };

  // Animations
  const sidebarVariants = {
    open: { x: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } },
    closed: { x: '-100%', transition: { type: 'spring', stiffness: 300, damping: 30 } }
  };

  const menuItemVariants = {
    open: { opacity: 1, y: 0, transition: { duration: 0.18 } },
    closed: { opacity: 0, y: -8, transition: { duration: 0.16 } }
  };

  // Inline style helpers to match Login.jsx design
  const styles = {
    aside: {
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 50,
      height: '100%',
      width: '18rem',
      backgroundColor: '#fff',
      borderRight: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform 0.3s ease-in-out'
    },
    logoRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '1.5rem',
      borderBottom: '1px solid #eaeef2'
    },
    logoBox: {
      width: '2.5rem',
      height: '2.5rem',
      backgroundColor: 'var(--primary-600, #3b82f6)',
      borderRadius: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 700,
      fontSize: '1rem'
    },
    userRow: {
      padding: '1rem 1.5rem',
      borderBottom: '1px solid #f3f4f6',
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'center'
    },
    avatar: {
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '9999px',
      backgroundColor: '#f3f4f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    nav: {
      flex: 1,
      overflowY: 'auto',
      padding: '0.75rem 0.75rem'
    },
    group: {
      padding: '0 0.75rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    link: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.625rem 0.75rem',
      borderRadius: '0.5rem',
      textDecoration: 'none',
      color: 'var(--gray-700, #374151)',
      fontSize: '0.9375rem',
      fontWeight: 500,
      transition: 'background-color 150ms, color 150ms'
    },
    linkHover: {
      backgroundColor: '#f8fafc',
      color: 'var(--gray-900, #111827)'
    },
    linkActive: {
      backgroundColor: 'rgba(59,130,246,0.08)', // bg-primary-50
      color: 'var(--primary-700, #1d4ed8)'
    },
    submenu: {
      marginLeft: '1.25rem',
      marginTop: '0.375rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    submenuLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.625rem',
      padding: '0.5rem 0.75rem',
      borderRadius: '0.5rem',
      textDecoration: 'none',
      color: 'var(--gray-600, #4b5563)',
      fontSize: '0.875rem',
      transition: 'background-color 150ms, color 150ms'
    },
    quickAction: {
      padding: '1rem',
      borderTop: '1px solid #eaeef2'
    },
    newTicket: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      width: '100%',
      padding: '0.625rem 1rem',
      backgroundColor: 'var(--primary-600, #3b82f6)',
      color: 'white',
      borderRadius: '0.5rem',
      fontWeight: 600,
      textDecoration: 'none',
      transition: 'background-color 150ms, transform 100ms'
    }
  };

  return (
    <>
      {/* Mobile overlay - only show on mobile when sidebar is open */}
      <AnimatePresence>
        {isOpen && !isDesktop && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 40
            }}
            onClick={onClose}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        style={{
          ...styles.aside,
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)'
        }}
        variants={sidebarVariants}
        animate={isOpen ? 'open' : 'closed'}
        initial={false}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <div style={styles.logoRow}>
          <div style={styles.logoBox} aria-hidden="true">RDX</div>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--gray-900)', fontSize: '1.125rem' }}>RoadExpress Freight</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Accelerator</div>
          </div>
        </div>

        {/* User Info */}
        <div style={styles.userRow}>
          <div style={styles.avatar}>
            <User className="user-icon" style={{ width: '1.25rem', height: '1.25rem', color: 'var(--gray-500, #6b7280)' }} aria-hidden="true" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--gray-900, #111827)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.first_name || ''} {user?.last_name || ''}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500, #6b7280)', textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.role || ''} • {user?.department || ''}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav style={styles.nav} aria-label="Sidebar">
          <div style={styles.group}>
            {filteredMenuItems.map((item, idx) => {
              const Icon = item.icon;
              const expanded = Boolean(expandedMenus[item.key]);
              const activeItem = item.href ? isActive(item.href) : (item.submenu && item.submenu.some(s => isActive(s.href)));
              return (
                <motion.div
                  key={item.key}
                  initial="closed"
                  animate="open"
                  variants={menuItemVariants}
                  transition={{ delay: idx * 0.04 }}
                >
                  {item.submenu ? (
                    <div>
                      <button
                        onClick={() => toggleMenu(item.key)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleMenu(item.key);
                          }
                        }}
                        aria-expanded={expanded}
                        aria-controls={`${item.key}-submenu`}
                        style={{
                          ...styles.link,
                          ...(activeItem ? styles.linkActive : {}),
                          background: 'transparent',
                          border: 'none',
                          width: '100%',
                          textAlign: 'left',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (!activeItem) {
                            e.currentTarget.style.backgroundColor = styles.linkHover.backgroundColor;
                            e.currentTarget.style.color = styles.linkHover.color;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!activeItem) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = styles.link.color;
                          }
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon style={{ width: '1.25rem', height: '1.25rem', color: activeItem ? 'var(--primary-700, #1d4ed8)' : 'var(--gray-600, #4b5563)' }} aria-hidden="true" />
                        </span>
                        <span style={{ flex: 1 }}>{item.label}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {expanded ? <ChevronDown style={{ width: '1rem', height: '1rem' }} /> : <ChevronRight style={{ width: '1rem', height: '1rem' }} />}
                        </span>
                      </button>

                      <AnimatePresence initial={false}>
                        {expanded && (
                          <motion.div
                            id={`${item.key}-submenu`}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.18 }}
                            style={styles.submenu}
                            aria-hidden={!expanded}
                          >
                            {item.submenu.map(sub => {
                              const SubIcon = sub.icon;
                              const subActive = isActive(sub.href);
                              return (
                                <Link
                                  key={sub.href}
                                  to={sub.href}
                                  onClick={handleLinkClick}
                                  style={{
                                    ...styles.submenuLink,
                                    ...(subActive ? styles.linkActive : {})
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!subActive) {
                                      e.currentTarget.style.backgroundColor = styles.linkHover.backgroundColor;
                                      e.currentTarget.style.color = styles.linkHover.color;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!subActive) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = styles.submenuLink.color;
                                    }
                                  }}
                                  aria-current={subActive ? 'page' : undefined}
                                >
                                  <SubIcon style={{ width: '1rem', height: '1rem', color: subActive ? 'var(--primary-700, #1d4ed8)' : 'var(--gray-500, #6b7280)' }} aria-hidden="true" />
                                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.label}</span>
                                </Link>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={handleLinkClick}
                      style={{
                        ...styles.link,
                        ...(activeItem ? styles.linkActive : {})
                      }}
                      onMouseEnter={(e) => {
                        if (!activeItem) {
                          e.currentTarget.style.backgroundColor = styles.linkHover.backgroundColor;
                          e.currentTarget.style.color = styles.linkHover.color;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!activeItem) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = styles.link.color;
                        }
                      }}
                      aria-current={activeItem ? 'page' : undefined}
                    >
                      <Icon style={{ width: '1.25rem', height: '1.25rem', color: activeItem ? 'var(--primary-700, #1d4ed8)' : 'var(--gray-600, #4b5563)' }} aria-hidden="true" />
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                    </Link>
                  )}
                </motion.div>
              );
            })}
          </div>
        </nav>

        {/* Quick action */}
        <div style={styles.quickAction}>
          <Link
            to="/tickets/create"
            onClick={handleLinkClick}
            style={styles.newTicket}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--primary-700, #1d4ed8)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--primary-600, #3b82f6)')}
          >
            <Plus style={{ width: '1rem', height: '1rem' }} aria-hidden="true" />
            <span>New Ticket</span>
          </Link>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar;